import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Bus, CheckCircle, XCircle, Clock, Calendar, User, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TransportSubscriptionValidation({ user }) {
  const [loading, setLoading] = useState(false);
  const [pendingSubscriptions, setPendingSubscriptions] = useState([]);

  useEffect(() => {
    fetchPendingSubscriptions();
  }, []);

  const fetchPendingSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/transport-subscriptions/pending`);
      setPendingSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching pending subscriptions:', error);
      toast.error('Erreur lors du chargement des abonnements en attente');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (subscriptionId) => {
    try {
      await axios.put(`${API}/transport-subscriptions/${subscriptionId}`, {
        status: 'ACTIVE'
      });
      
      toast.success('Abonnement validé avec succès');
      fetchPendingSubscriptions();
    } catch (error) {
      console.error('Error validating subscription:', error);
      toast.error('Erreur lors de la validation');
    }
  };

  const handleReject = async (subscriptionId) => {
    try {
      await axios.put(`${API}/transport-subscriptions/${subscriptionId}`, {
        status: 'REJECTED'
      });
      
      toast.success('Abonnement rejeté');
      fetchPendingSubscriptions();
    } catch (error) {
      console.error('Error rejecting subscription:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Validation des Abonnements Transport</h1>
          <p className="text-gray-600 mt-1">
            Validez ou rejetez les demandes d'abonnement au transport scolaire
          </p>
        </div>
        <Button onClick={fetchPendingSubscriptions} variant="outline">
          <Clock className="w-4 h-4 mr-2" />
          Rafraîchir
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-orange-600">{pendingSubscriptions.length}</p>
              </div>
              <Clock className="h-10 w-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des abonnements en attente */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : pendingSubscriptions.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Aucun abonnement en attente</p>
              <p className="text-sm mt-2">Tous les abonnements ont été traités</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingSubscriptions.map((subscription) => (
            <Card key={subscription.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Bus className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{subscription.route_name}</CardTitle>
                      <CardDescription className="flex items-center space-x-2 mt-1">
                        <User className="h-4 w-4" />
                        <span>{subscription.student_name}</span>
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                    <Clock className="h-3 w-3 mr-1" />
                    En attente
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Informations */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date de début</p>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <p className="text-sm font-medium">{formatDate(subscription.start_date)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date de fin</p>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <p className="text-sm font-medium">{formatDate(subscription.end_date)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Prix mensuel</p>
                    <p className="text-sm font-medium">{formatPrice(subscription.monthly_price)} GNF</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Arrêt</p>
                    <div className="flex items-center space-x-1">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <p className="text-sm font-medium">{subscription.pickup_stop || 'Non spécifié'}</p>
                    </div>
                  </div>
                </div>

                {/* Notes spéciales */}
                {subscription.special_notes && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <strong>Note :</strong> {subscription.special_notes}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(subscription.id)}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeter
                  </Button>
                  <Button
                    onClick={() => handleValidate(subscription.id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Valider
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default TransportSubscriptionValidation;
